import { all, takeLatest, call, put } from "redux-saga/effects";

import {
  setMandatoryDocumentsStatus,
  setMandatoryDocumentsLoading,
  setDocumentsList
} from "./actions";
import {
  checkMandatoryDocumentsService,
  registerDocumentDownloadAttemptService,
} from "../../../service/documentRequired.service";

/**
 * Verifica se há documentos obrigatórios pendentes ou rejeitados não reenviados.
 * @param {Object} documents - Lista de documentos.
 * @returns {boolean} - True se houver obrigatórios pendentes ou rejeitados não reenviados.
 * Chaves de tradução sugeridas: 'document.status.rejected', 'document.mandatory'
 */
const hasMandatoryDocuments = ({ pendingDocs = [], submittedDocs = [] }) => {
  // Verifica obrigatórios na lista de pendentes
  const hasPendingMandatory = pendingDocs.some(({ mandatory }) => mandatory);

  // Verifica obrigatórios rejeitados não reenviados na lista de enviados
  const hasRejectedMandatory = submittedDocs.some(
    ({ mandatory, resent, status }) =>
      mandatory && !resent && status === 'REJECTED'
  );

  return hasPendingMandatory || hasRejectedMandatory;
};

function* checkMandatoryDocuments({ payload }) {
  try {
    if (payload.opportunityId) {
      yield put(setMandatoryDocumentsLoading(true));

      const response = yield call(
        checkMandatoryDocumentsService,
        payload.opportunityId
      );

      if (response.status === 200) {
        const documents = response.data.data || response.data.output || response.data;

        if (!documents) {
          yield put(setMandatoryDocumentsStatus(false));
          return
        }

        const hasMandatory = hasMandatoryDocuments(documents);

        yield put(setMandatoryDocumentsStatus(hasMandatory));

        if (payload.callbackSuccess) {
          payload.callbackSuccess(hasMandatory);
        }
      }
    } else {
      yield put(setMandatoryDocumentsStatus(false));
    }
  } catch (error) {
    yield put(setMandatoryDocumentsStatus(false));

    if (!payload.opportunityId) {
      return
    }

    if (payload.callbackError) {
      const errorCode = error.response?.data?.code || error.message;
      payload.callbackError(errorCode);
    }
  } finally {
    yield put(setMandatoryDocumentsLoading(false));
  }
}

function* fetchDocumentsList({ payload }) {
  try {
    if (payload.opportunityId) {
      const response = yield call(
        checkMandatoryDocumentsService,
        payload.opportunityId
      );

      if (response.status === 200) {
        const documents = response.data ?? [];

        yield put(setDocumentsList(documents));

        const hasMandatory = hasMandatoryDocuments(documents);

        yield put(setMandatoryDocumentsStatus(hasMandatory));

        if (payload.callbackSuccess) {
          payload.callbackSuccess(documents);
        }
      }
    } else {
      yield put(setDocumentsList(null));
    }
  } catch (error) {
    yield put(setDocumentsList(null));

    if (!payload.opportunityId) {
      return
    }

    if (payload.callbackError) {
      const errorCode = error.response?.data?.code || error.message;
      payload.callbackError(errorCode);
    }
  }
}

function* registerDocumentDownloadAttempt({ payload }) {
  try {
    yield call(
      registerDocumentDownloadAttemptService,
      payload.params
    );
  } catch (error) {
    const errorCode = error.response?.data?.code || error.message;
    payload.callbackError(errorCode);
  }
}

export default all([
  takeLatest("CHECK_MANDATORY_DOCUMENTS", checkMandatoryDocuments),
  takeLatest("FETCH_DOCUMENTS_LIST", fetchDocumentsList),
  takeLatest("REGISTER_DOCUMENT_DOWNLOAD_ATTEMPT", registerDocumentDownloadAttempt),
]);